import { View } from 'react-native'
import Animated, {
  interpolateColor,
  useAnimatedStyle,
} from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { AnimatedPressable } from '@ui/animated/nativewindReanimated'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import BlurEffect from '@ui/effects/BlurEffect'
import { colors } from '@ui/colors'
import Price from '@/price/Price'
import Typography from '@ui/typography'
import { leadingNone } from '@ui/typography/styles'
import useDealSettings from '@order/hooks/useDealSettings'
import useOrderTotal from '@order/hooks/useOrderTotal'

interface Props {
  price: number
  disabled?: boolean
  onPress: () => void
}

export default function CartButtonPrice({ price, disabled, onPress }: Props) {
  const { t } = useTranslation(['steps'])
  const { priceSuffix: installment } = useDealSettings()
  const total = useOrderTotal()

  const {
    animation: animationPress,
    onPressIn,
    onPressOut,
  } = useAnimatedPress()

  const styleOpacity = useAnimatedOpacity(!disabled)

  const stylePressable = useAnimatedStyle(() => ({
    borderWidth: 2,
    borderColor: interpolateColor(
      animationPress.value,
      [0, 1],
      [colors.text, colors.text0]
    ),
    backgroundColor: interpolateColor(
      animationPress.value,
      [0, 1],
      [colors.text10, colors.text]
    ),
  }))

  const styleText = useAnimatedStyle(() => ({
    color: interpolateColor(
      animationPress.value,
      [0, 1],
      [colors.text, colors.white]
    ),
  }))

  return (
    <AnimatedPressable
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      disabled={disabled}
      style={styleOpacity}
    >
      <Animated.View
        className="flex items-center justify-center rounded-full"
        style={[stylePressable, { height: 32, minWidth: 80 }]}
      >
        <BlurEffect
          intensity={50}
          tint="light"
          className="h-full w-full rounded-full"
        />
        <View className="flex flex-row items-center gap-0 px-4">
          <Typography variant="MDB" className={leadingNone} style={styleText}>
            <Price
              noPrecision
              showZero
              value={price ? Math.max(price, 0) : undefined}
            />
          </Typography>
          {!!installment && total.showMonthly && (
            <Typography variant="SMB" className={leadingNone}>
              {t(`steps:priceSuffix.${installment}.short`)}
            </Typography>
          )}
        </View>
      </Animated.View>
    </AnimatedPressable>
  )
}
